import * as React from "react";
import { Toggle as TogglePrimitive } from "@base-ui/react/toggle";
import { ToggleGroup as ToggleGroupPrimitive } from "@base-ui/react/toggle-group";
import { type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";
import { fromArrayValue, toArrayValue } from "../../lib/single-select-value";
import { toggleVariants } from "./toggle";

/**
 * Migrated from `@radix-ui/react-toggle-group` to Base UI, together with
 * `toggle.tsx` — see that file for why they are one PR.
 *
 * The public API of both exports is unchanged: 17 files consume this component
 * and none of them changes. Three Base UI differences are absorbed here.
 *
 *  1. **`type` → `multiple`.** Radix discriminates on
 *     `type="single" | "multiple"` and types the value as a bare string in one
 *     mode and an array in the other; Base UI has a `multiple` boolean and an
 *     array in both. 18 of the 19 call sites are `single`, so the wrapper keeps
 *     Radix's discriminated union and normalizes at the boundary — the same
 *     shape Accordion needed (#6176), which is why the conversion now lives in
 *     `lib/single-select-value.ts` rather than being written out twice.
 *  2. **There is no `ToggleGroup.Item`.** Base UI's group takes plain `Toggle`
 *     children carrying a `value`. `ToggleGroupItem` keeps its name, its
 *     `data-slot="toggle-group-item"`, and its context-inherited `variant` /
 *     `size`, and renders one.
 *  3. **`onValueChange` gains a second argument.** Base UI passes `eventDetails`
 *     alongside the value; forwarding it would widen the callback every caller
 *     is typed against.
 *
 * Two differences are **not** absorbed, and both reach users:
 *
 *  - **`data-state="on"` → `data-pressed`**, which lives in `toggleVariants`.
 *    `ReplenishmentPage` overrides the pressed fill through it and is updated in
 *    the same commit.
 *  - **Single mode is no longer a radio group.** Radix rendered `type="single"`
 *    items as `role="radio"` with `aria-checked`, and `type="multiple"` items as
 *    buttons with `aria-pressed`. Base UI's group has no item part of its own,
 *    so both modes are now `aria-pressed` buttons. This is a real change for
 *    screen-reader users across 18 call sites and is flagged in the PR rather
 *    than papered over: emitting `role="radio"` on top of the `aria-pressed`
 *    Base UI already sets would produce contradictory ARIA, which is worse than
 *    the honest change.
 */

const ToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants>
>({
  size: "default",
  variant: "default",
});

/** Props shared by both modes — Base UI's group minus everything value-shaped. */
type ToggleGroupRootProps = Omit<
  ToggleGroupPrimitive.Props<string>,
  "value" | "defaultValue" | "onValueChange" | "multiple" | "className"
> &
  VariantProps<typeof toggleVariants> & { className?: string };

type ToggleGroupSingleProps = ToggleGroupRootProps & {
  type: "single";
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
};

type ToggleGroupMultipleProps = ToggleGroupRootProps & {
  type: "multiple";
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
};

type ToggleGroupProps = ToggleGroupSingleProps | ToggleGroupMultipleProps;

/**
 * The group's own classes are unchanged. `data-variant` / `data-size` are this
 * component's attributes rather than the primitive's, so the selectors keyed on
 * them (`data-[variant=outline]:shadow-xs` here, `data-[variant=outline]:border-l-0`
 * on the item) survive the migration untouched.
 */
const groupClassName = (className: string | undefined) =>
  cn(
    // `min-w-max` here and on each item keeps every label whole: a caller's
    // width can grow the group, but never squeeze an item below its label. A
    // group the caller lets wrap (`flex-wrap`) skips the group minimum, so it
    // stays inside its container and wraps whole items instead.
    "group/toggle-group flex w-fit not-[.flex-wrap]:min-w-max items-center rounded-md data-[variant=outline]:shadow-xs",
    "border [&>*:not(:last-child)]:border-r",
    className,
  );

function ToggleGroup(props: ToggleGroupProps) {
  // Memoized, and read off `props` before the branch below so the hook runs
  // unconditionally. Built inline, this object is a new reference on every
  // render of the group, which re-renders every item through the context even
  // when `variant` and `size` never change.
  //
  // Read through `props` rather than destructured here on purpose: each branch
  // still has to destructure `variant` and `size` out of its own `...rest`, or
  // they are spread onto the primitive and land on the DOM as stray attributes.
  const itemVariants = React.useMemo(
    () => ({ variant: props.variant, size: props.size }),
    [props.variant, props.size],
  );

  if (props.type === "multiple") {
    const {
      // Radix's discriminator, destructured away so it cannot land on the DOM.
      type: _type,
      className,
      variant,
      size,
      children,
      value,
      defaultValue,
      onValueChange,
      ...rest
    } = props;

    return (
      <ToggleGroupPrimitive
        data-slot="toggle-group"
        data-variant={variant}
        data-size={size}
        className={groupClassName(className)}
        multiple
        value={value}
        defaultValue={defaultValue}
        onValueChange={(next) => onValueChange?.(next)}
        {...rest}
      >
        <ToggleGroupContext.Provider value={itemVariants}>
          {children}
        </ToggleGroupContext.Provider>
      </ToggleGroupPrimitive>
    );
  }

  const {
    type: _type,
    className,
    variant,
    size,
    children,
    value,
    defaultValue,
    onValueChange,
    ...rest
  } = props;

  return (
    <ToggleGroupPrimitive
      data-slot="toggle-group"
      data-variant={variant}
      data-size={size}
      className={groupClassName(className)}
      value={toArrayValue(value)}
      defaultValue={toArrayValue(defaultValue)}
      onValueChange={(next) => onValueChange?.(fromArrayValue(next))}
      {...rest}
    >
      <ToggleGroupContext.Provider value={itemVariants}>
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive>
  );
}

/**
 * Base UI has no `ToggleGroup.Item`, so this renders its `Toggle` with a
 * `value`. `value` stays required, as Radix had it — Base UI would otherwise
 * fall back to an index, which changes an item's identity whenever the list
 * around it is filtered.
 */
type ToggleGroupItemProps = Omit<
  TogglePrimitive.Props<string>,
  "value" | "className"
> &
  VariantProps<typeof toggleVariants> & {
    value: string;
    className?: string;
  };

function ToggleGroupItem({
  className,
  children,
  variant,
  size,
  ...props
}: ToggleGroupItemProps) {
  const context = React.useContext(ToggleGroupContext);

  return (
    <TogglePrimitive
      data-slot="toggle-group-item"
      data-variant={context.variant || variant}
      data-size={context.size || size}
      className={cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
        }),
        "min-w-max flex-1 shrink-0 rounded-none shadow-none first:rounded-l-md last:rounded-r-md focus:z-10 focus-visible:z-10 data-[variant=outline]:border-l-0 data-[variant=outline]:first:border-l",
        className,
      )}
      {...props}
    >
      {children}
    </TogglePrimitive>
  );
}

export { ToggleGroup, ToggleGroupItem };
